/**
 * One place to save a file the app produced or fetched (a generated PDF, a
 * chat attachment, …).
 *
 * - **Browser:** the classic object-URL + `<a download>` click — the browser
 *   routes it to the user's configured Downloads folder.
 * - **Desktop app (Tauri):** invokes the Rust `save_download` command, which
 *   shows the **native OS "Save As" dialog** so the user CHOOSES where the file
 *   goes (X12 — before this, the webview dropped downloads in an opaque
 *   default location). On any failure (older shell without the command, a
 *   write error) it falls back to the browser path, so a download never
 *   silently fails.
 *
 * Standalone (its own `isTauri`, no imports) so it's safe to use from anywhere,
 * including the walled CRM sub-app.
 */

function isTauri(): boolean {
  return (
    typeof window !== "undefined" &&
    ("__TAURI_INTERNALS__" in window || "__TAURI__" in window)
  );
}

/** Browser download via a temporary object URL + anchor click. */
function anchorDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename || "download";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the download has had a chance to start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Save `blob` under `filename`. In the desktop app this opens a native Save-As
 * dialog (the user picks the location); in the browser it downloads normally.
 * Resolves once the save is handled (or the user cancelled the dialog).
 */
export async function saveBlob(filename: string, blob: Blob): Promise<void> {
  if (isTauri()) {
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const bytes = Array.from(new Uint8Array(await blob.arrayBuffer()));
      // Returns true if saved, false if the user cancelled — either way the
      // native dialog handled it, so we do NOT also browser-download.
      await invoke("save_download", { filename, bytes });
      return;
    } catch (e) {
      // Older shell without the command, or a write failure — fall through to
      // the browser path so the download still happens (no regression).
      console.warn("[desktop] native save failed; falling back to browser download:", e);
    }
  }
  anchorDownload(filename, blob);
}
